/**
 * Icon Scale Calculator
 *
 * Automatically calculates appropriate icon scales based on floor plan dimensions.
 * Uses empirically-derived reference diagonals from calibration data.
 *
 * Calibration source: 5 floor plans with manually-calibrated elements
 *
 * Key findings from calibration:
 * - Fixtures: Users prefer ~1.0 on large floor plans (4500-8300px diagonal)
 *   but smaller floor plans need smaller scales to maintain visual proportion
 * - Controls: Scale increases with floor plan size (1.9 at 4500px → 2.7 at 8300px diagonal)
 * - LV Products: Scale increases with floor plan size (1.4 at 4500px → 2.4 at 8300px diagonal)
 *
 * Reference Diagonals (where scale = 1.0):
 * - Fixtures: 4500px - keeps large floor plans at ~1.0, scales down smaller ones
 * - Controls: 3000px (calibrated from avg implied reference of 2869)
 * - LV Products: 3800px (calibrated from avg implied reference of 3850)
 */

export type IconDomain = 'fixtures' | 'controls' | 'lv_products';

interface DomainScaleConfig {
  referenceDiagonal: number | null; // null means no auto-scale (always 1.0)
  minScale: number;
  maxScale: number;
}

/**
 * Empirically-derived configuration from calibration exercise
 * Based on user preference data across floor plans of varying sizes (diagonal 4498-8314px)
 */
const DOMAIN_CONFIGS: Record<IconDomain, DomainScaleConfig> = {
  fixtures: {
    // Fixtures: Users want ~1.0 on large floor plans (4500-8300px diagonal)
    // Reference of 4500px keeps large plans at 1.0+, scales down smaller plans appropriately
    // Example: Chow floor plan (3544px diagonal) → 3544/4500 ≈ 0.8
    referenceDiagonal: 4500,
    minScale: 0.3,
    maxScale: 3.0,
  },
  controls: {
    // Controls: Average implied reference from calibration was 2869, rounded to 3000
    referenceDiagonal: 3000,
    minScale: 0.3,
    maxScale: 3.0,
  },
  lv_products: {
    // LV Products: Average implied reference from calibration was 3850, using 3800
    referenceDiagonal: 3800,
    minScale: 0.3,
    maxScale: 3.0,
  },
};

/**
 * Calculate the diagonal of an image from its dimensions
 */
export function calculateDiagonal(width: number, height: number): number {
  return Math.sqrt(width ** 2 + height ** 2);
}

/**
 * Calculate the auto-scale for a specific domain based on floor plan dimensions
 *
 * @param domain - The icon domain (fixtures, controls, or lv_products)
 * @param imageWidth - Floor plan image width in pixels
 * @param imageHeight - Floor plan image height in pixels
 * @returns Scale factor (0.3 to 3.0), rounded to 1 decimal place
 *
 * @example
 * // Small floor plan (2000x2000, diagonal ~2828px)
 * calculateDomainScale('fixtures', 2000, 2000)  // Returns 0.6 (2828/4500)
 * calculateDomainScale('controls', 2000, 2000)  // Returns 0.9 (2828/3000)
 *
 * @example
 * // Medium floor plan - Chow (2800x2200, diagonal ~3544px)
 * calculateDomainScale('fixtures', 2800, 2200)  // Returns 0.8 (3544/4500)
 * calculateDomainScale('controls', 2800, 2200)  // Returns 1.2 (3544/3000)
 *
 * @example
 * // Large floor plan (6000x4000, diagonal ~7211px)
 * calculateDomainScale('fixtures', 6000, 4000)  // Returns 1.6 (7211/4500)
 * calculateDomainScale('controls', 6000, 4000)  // Returns 2.4 (7211/3000)
 */
export function calculateDomainScale(
  domain: IconDomain,
  imageWidth: number,
  imageHeight: number,
): number {
  const config = DOMAIN_CONFIGS[domain];

  // If no reference diagonal, domain doesn't auto-scale (always 1.0)
  if (config.referenceDiagonal === null) {
    return 1.0;
  }

  const diagonal = calculateDiagonal(imageWidth, imageHeight);
  const rawScale = diagonal / config.referenceDiagonal;

  // Round to 1 decimal place and clamp to valid range
  return Math.round(Math.max(config.minScale, Math.min(config.maxScale, rawScale)) * 10) / 10;
}

/**
 * Calculate auto-scales for all three domains at once
 *
 * @param imageWidth - Floor plan image width in pixels
 * @param imageHeight - Floor plan image height in pixels
 * @returns Object with scale factors for all domains
 *
 * @example
 * const scales = calculateAllDomainScales(4000, 3000);
 * // Returns: { fixtures: 0.8, controls: 1.8, lv_products: 1.6 }
 */
export function calculateAllDomainScales(
  imageWidth: number,
  imageHeight: number,
): { fixtures: number; controls: number; lv_products: number } {
  return {
    fixtures: calculateDomainScale('fixtures', imageWidth, imageHeight),
    controls: calculateDomainScale('controls', imageWidth, imageHeight),
    lv_products: calculateDomainScale('lv_products', imageWidth, imageHeight),
  };
}

/**
 * Convert a scale factor to percentage for display
 * @example scaleToPercentage(1.5) // Returns 150
 */
export function scaleToPercentage(scale: number): number {
  return Math.round(scale * 100);
}

/**
 * Convert a percentage to scale factor
 * @example percentageToScale(150) // Returns 1.5
 */
export function percentageToScale(percentage: number): number {
  return percentage / 100;
}

/**
 * Get the reference diagonal for a specific domain
 * Useful for debugging or display purposes
 * Returns null if domain doesn't auto-scale
 */
export function getReferenceDiagonal(domain: IconDomain): number | null {
  return DOMAIN_CONFIGS[domain].referenceDiagonal;
}

/**
 * Get scale limits for validation
 */
export function getScaleLimits(): { min: number; max: number } {
  return { min: 0.3, max: 3.0 };
}
